import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
} from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ChevronDown, ChevronUp, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  GameAnalysisFilters,
  setFilters,
} from '@/redux/slices/gameAnalysisSlice'
import { Checkbox } from '@/components/ui/Checkbox'
import { getPlayerName } from '@/utils/playerUtils'
import { useAppDispatch } from '@/redux/hooks'
import { GameSummaryMetadata } from '@/redux/types/room'

interface FilterSelectorProps {
  metadata: GameSummaryMetadata | null
  selectedFilters: GameAnalysisFilters
  onClearFilters: () => void
}

const FilterSelector: React.FC<FilterSelectorProps> = ({
  metadata,
  selectedFilters,
  onClearFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false)
      }
    },
    [isOpen]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  const hasActiveFilters = useMemo(
    () =>
      selectedFilters.player_ids.length > 0 ||
      selectedFilters.team_ids.length > 0 ||
      selectedFilters.phase_ids.length > 0 ||
      selectedFilters.round_numbers.length > 0,
    [
      selectedFilters.player_ids.length,
      selectedFilters.team_ids.length,
      selectedFilters.phase_ids.length,
      selectedFilters.round_numbers.length,
    ]
  )

  const handleFiltersChange = useCallback(
    (filters: typeof selectedFilters) => {
      dispatch(setFilters(filters))
    },
    [dispatch]
  )

  const handlePlayerToggle = useCallback(
    (playerId: number) => {
      const currentIds = selectedFilters.player_ids
      const newIds = currentIds.includes(playerId)
        ? currentIds.filter((id) => id !== playerId)
        : [...currentIds, playerId]
      handleFiltersChange({ ...selectedFilters, player_ids: newIds })
    },
    [selectedFilters, handleFiltersChange]
  )

  const handleTeamToggle = useCallback(
    (teamId: number) => {
      const currentIds = selectedFilters.team_ids
      const newIds = currentIds.includes(teamId)
        ? currentIds.filter((id) => id !== teamId)
        : [...currentIds, teamId]
      handleFiltersChange({ ...selectedFilters, team_ids: newIds })
    },
    [selectedFilters, handleFiltersChange]
  )

  const handlePhaseToggle = useCallback(
    (phaseId: number) => {
      const currentIds = selectedFilters.phase_ids
      const newIds = currentIds.includes(phaseId)
        ? currentIds.filter((id) => id !== phaseId)
        : [...currentIds, phaseId]
      handleFiltersChange({ ...selectedFilters, phase_ids: newIds })
    },
    [selectedFilters, handleFiltersChange]
  )

  const handleRoundToggle = useCallback(
    (roundNumber: number) => {
      const currentIds = selectedFilters.round_numbers
      const newIds = currentIds.includes(roundNumber)
        ? currentIds.filter((id) => id !== roundNumber)
        : [...currentIds, roundNumber]
      handleFiltersChange({ ...selectedFilters, round_numbers: newIds })
    },
    [selectedFilters, handleFiltersChange]
  )

  if (!metadata) {
    return null
  }

  return (
    <div ref={filterRef} className='border rounded-lg bg-white'>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className='w-full flex items-center justify-between p-2 hover:bg-gray-50 transition-colors'>
          <div className='flex items-center gap-2'>
            <Filter className='h-4 w-4 text-gray-600' />
            <span className='font-medium text-gray-900'>Filters</span>
            {hasActiveFilters && (
              <span className='px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full'>
                Active
              </span>
            )}
          </div>
          {isOpen ? (
            <ChevronUp className='h-4 w-4 text-gray-600' />
          ) : (
            <ChevronDown className='h-4 w-4 text-gray-600' />
          )}
        </CollapsibleTrigger>

        <CollapsibleContent className='px-4 pb-4 space-y-4'>
          {/* Players Filter */}
          <div>
            <label className='text-sm font-medium text-gray-700 mb-2 block'>
              Players ({selectedFilters.player_ids.length} selected)
            </label>
            <div className='max-h-40 overflow-y-auto border rounded-md p-2 space-y-2'>
              {metadata.players.length === 0 ? (
                <p className='text-sm text-gray-500'>No players found</p>
              ) : (
                metadata.players.map((player) => (
                  <div key={player.id} className='flex items-center space-x-2'>
                    <Checkbox
                      checked={selectedFilters.player_ids.includes(
                        player.member__id
                      )}
                      onChange={() => handlePlayerToggle(player.member__id)}
                    />
                    <label className='text-sm text-gray-700 cursor-pointer flex-1'>
                      {getPlayerName(player)}
                      {player.team__name && (
                        <span className='text-xs text-gray-500 ml-2'>
                          ({player.team__name})
                        </span>
                      )}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Teams Filter */}
          <div>
            <label className='text-sm font-medium text-gray-700 mb-2 block'>
              Teams ({selectedFilters.team_ids.length} selected)
            </label>
            <div className='max-h-40 overflow-y-auto border rounded-md p-2 space-y-2'>
              {metadata.teams.length === 0 ? (
                <p className='text-sm text-gray-500'>No teams found</p>
              ) : (
                metadata.teams.map((team) => (
                  <div key={team.id} className='flex items-center space-x-2'>
                    <Checkbox
                      checked={selectedFilters.team_ids.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                    />
                    <label className='text-sm text-gray-700 cursor-pointer flex-1'>
                      {team.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Phases Filter */}
          <div>
            <label className='text-sm font-medium text-gray-700 mb-2 block'>
              Phases ({selectedFilters.phase_ids.length} selected)
            </label>
            <div className='max-h-40 overflow-y-auto border rounded-md p-2 space-y-2'>
              {metadata.phases.length === 0 ? (
                <p className='text-sm text-gray-500'>No phases found</p>
              ) : (
                metadata.phases.map((phase) => (
                  <div key={phase.id} className='flex items-center space-x-2'>
                    <Checkbox
                      checked={selectedFilters.phase_ids.includes(phase.id)}
                      onChange={() => handlePhaseToggle(phase.id)}
                    />
                    <label className='text-sm text-gray-700 cursor-pointer flex-1'>
                      Round {phase.game_round__round_number} -{' '}
                      {phase.phase_type}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Rounds Filter */}
          <div>
            <label className='text-sm font-medium text-gray-700 mb-2 block'>
              Rounds ({selectedFilters.round_numbers.length} selected)
            </label>
            <div className='max-h-40 overflow-y-auto border rounded-md p-2 space-y-2'>
              {metadata.rounds.length === 0 ? (
                <p className='text-sm text-gray-500'>No rounds found</p>
              ) : (
                metadata.rounds.map((round) => (
                  <div
                    key={round.round_number}
                    className='flex items-center space-x-2'
                  >
                    <Checkbox
                      checked={selectedFilters.round_numbers.includes(
                        round.round_number
                      )}
                      onChange={() => handleRoundToggle(round.round_number)}
                    />
                    <label className='text-sm text-gray-700 cursor-pointer flex-1'>
                      Round {round.round_number} ({round.status})
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className='pt-2 border-t'>
              <Button
                variant='outline'
                size='sm'
                onClick={onClearFilters}
                className='w-full'
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

export default memo(FilterSelector)
